import { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { ZodError } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import {
  getPublication,
  updatePublication,
  archivePublication,
  publicationKinds,
  publicationSchema,
} from '@/lib/api/publications';
import { Logo } from '@/components/ui/Logo';

const KIND_LABEL: Record<string, string> = {
  article: 'Article', essay: 'Essay', book: 'Book', series: 'Series',
};

export default function EditPublicationPage() {
  const { status } = useAuth();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: pub, isLoading, isError } = useQuery({
    queryKey: ['publication', id],
    queryFn: () => getPublication(id),
  });

  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'article' | 'essay' | 'book' | 'series'>('article');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (pub) {
      setTitle(pub.title);
      setKind(pub.kind as typeof kind);
      setVisibility(pub.visibility as typeof visibility);
    }
  }, [pub]);

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  function validate() {
    if (!pub) return false;
    try {
      publicationSchema.parse({ title, slug: pub.slug, kind, visibility });
      setFieldErrors({});
      return true;
    } catch (e) {
      if (e instanceof ZodError) {
        const errs: Record<string, string> = {};
        for (const issue of e.errors) {
          if (issue.path[0]) errs[String(issue.path[0])] = issue.message;
        }
        setFieldErrors(errs);
      }
      return false;
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      await updatePublication(id, { title, kind, visibility });
      queryClient.invalidateQueries({ queryKey: ['publication', id] });
      queryClient.invalidateQueries({ queryKey: ['my-publications'] });
      history.goBack();
    } catch (err: unknown) {
      const pgErr = err as { message?: string };
      setError(pgErr?.message ?? (err instanceof Error ? err.message : 'Something went wrong.'));
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!pub) return;
    if (!window.confirm(`Archive "${pub.title}"? It will no longer be visible to readers.`)) return;
    setArchiving(true);
    try {
      await archivePublication(id);
      queryClient.invalidateQueries({ queryKey: ['my-publications'] });
      history.replace('/publications');
    } catch (err: unknown) {
      const pgErr = err as { message?: string };
      setError(pgErr?.message ?? (err instanceof Error ? err.message : 'Something went wrong.'));
      setArchiving(false);
    }
  }

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
            <span className="text-sm font-semibold text-gray-700">Edit publication</span>
          </div>
        </header>

        <main className="mx-auto max-w-2xl px-5 py-10">
          {isLoading && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {isError && (
            <p className="py-20 text-center text-gray-500">Publication not found.</p>
          )}

          {pub && (
            <form onSubmit={save} className="flex flex-col gap-6">
              {/* Slug — immutable */}
              <div className="rounded-lg bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-500">Slug (cannot be changed)</p>
                <p className="mt-0.5 font-mono text-sm text-gray-700">/{pub.slug}</p>
              </div>

              {/* Title */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  maxLength={200}
                  className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                />
                {fieldErrors.title && (
                  <p className="text-xs text-red-500">{fieldErrors.title}</p>
                )}
              </div>

              {/* Kind */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Kind</label>
                <div className="flex flex-wrap gap-2">
                  {publicationKinds.map(k => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                        kind === k
                          ? 'border-[#2f3e46] bg-[#2f3e46] text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {KIND_LABEL[k]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="visibility">
                  Visibility
                </label>
                <select
                  id="visibility"
                  value={visibility}
                  onChange={e => setVisibility(e.target.value as typeof visibility)}
                  className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                >
                  <option value="public">Public — visible to everyone</option>
                  <option value="unlisted">Unlisted — accessible by link only</option>
                  <option value="private">Private — only you can see it</option>
                </select>
              </div>

              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-[#2f3e46] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <div className="ml-auto">
                  <button
                    type="button"
                    onClick={archive}
                    disabled={archiving}
                    className="rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 disabled:opacity-40"
                  >
                    {archiving ? 'Archiving…' : 'Archive'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </main>
      </IonContent>
    </IonPage>
  );
}
